import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type AdminFeedback,
  adminFeedbackParams,
  FEEDBACK_STATUSES,
  type FeedbackKind,
  type FeedbackStatus,
  useAdminFeedback,
  useDeleteFeedback,
  useUpdateFeedback,
} from '../../api/feedback';
import { Dialog } from '../../components/Dialog';
import { statusChipClass } from '../../components/FeedbackDialog';
import { FilterChips } from '../../components/FilterChips';
import { ErrorView } from '../../components/Guards';
import { dateLocale } from '../../i18n';
import { formatDate } from '../../lib/format';
import ui from '../../styles/ui.module.css';
import { browserSummary } from '../../lib/browser';
import { apiErrorText } from './adminFormat';
import styles from './AdminPages.module.css';

/**
 * The administrator's feedback: every defect report and suggestion, newest first, with who sent it
 * and the context the app added. Each gets a status and an answer, which its sender sees.
 */
export function AdminFeedbackPage() {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState<FeedbackKind | null>(null);
  const [status, setStatus] = useState<FeedbackStatus | null>(null);
  const list = useAdminFeedback(status, kind, query);
  const all = useAdminFeedback(null, kind, query);
  const counted = all.data ?? [];
  const items = list.data ?? [];

  return (
    <div className={`${ui.page} ${styles.page}`}>
      <div className={ui.pageHeader}>
        <div>
          <h1>{t('admin.feedback.title')}</h1>
          <div className={ui.subtitle}>{t('admin.feedback.subtitle')}</div>
          <div className={ui.subtitle}>{t('myFeedback.retention')}</div>
        </div>
        <a className={ui.button} href={`/api/admin/feedback.csv?${adminFeedbackParams(status, kind, query)}`} download data-testid="feedback-csv">
          {t('admin.audit.csv')}
        </a>
      </div>
      <div className={styles.listColumn}>
        <div className={styles.auditFilters}>
          <input
            className={ui.input}
            type="search"
            placeholder={t('admin.feedback.search')}
            aria-label={t('admin.feedback.search')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <FilterChips
          label={t('admin.feedback.kind')}
          value={kind}
          onChange={setKind}
          testId="feedback-kind-filters"
          options={[
            { key: null, label: t('events.all'), count: counted.length },
            ...(['DEFECT', 'SUGGESTION'] as const).map((k) => ({
              key: k,
              label: t(`admin.feedback.kinds.${k}`),
              count: counted.filter((f) => f.kind === k).length,
            })),
          ]}
        />
        <FilterChips
          label={t('admin.feedback.status')}
          value={status}
          onChange={setStatus}
          testId="feedback-status-filters"
          options={[
            { key: null, label: t('events.all'), count: counted.length },
            ...FEEDBACK_STATUSES.map((s) => ({ key: s, label: t(`feedback.status.${s}`), count: counted.filter((f) => f.status === s).length })),
          ]}
        />
        {list.isError && <ErrorView onRetry={() => void list.refetch()} />}
        {list.isPending && <p className={ui.muted}>{t('common.loading')}</p>}
        {list.data && items.length === 0 && (
          <section className={ui.card}>
            <p className={ui.muted}>{t('admin.feedback.none')}</p>
          </section>
        )}
        {items.map((f) => (
          <FeedbackCard key={`${f.id}-${f.updatedAt}`} item={f} />
        ))}
      </div>
    </div>
  );
}

function FeedbackCard({ item: f }: { item: AdminFeedback }) {
  const { t } = useTranslation();
  const update = useUpdateFeedback();
  const remove = useDeleteFeedback();
  const [deleting, setDeleting] = useState(false);
  const [status, setStatus] = useState<FeedbackStatus>(f.status);
  const [note, setNote] = useState(f.adminNote ?? '');
  const changed = status !== f.status || note.trim() !== (f.adminNote ?? '');
  const when = new Intl.DateTimeFormat(dateLocale(), { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }).format(new Date(f.createdAt));
  const who = [f.choirName, f.role ? t(`roles.${f.role}`) : null, f.part ? t(`parts.${f.part}`) : null].filter(Boolean).join(' · ');
  const context = [f.page, f.appVersion ? `${t('admin.feedback.version')} ${f.appVersion}` : null, browserSummary(f.userAgent), f.screen, f.locale]
    .filter(Boolean)
    .join(' · ');

  return (
    <article className={`${ui.card} ${styles.feedbackCard}`} data-testid="admin-feedback-item">
      <div className={styles.feedbackHead}>
        <div>
          <strong>{f.userName}</strong> <span className={ui.muted}>{who ? `· ${who}` : ''}</span>
          <div className={ui.secondary}>
            {when} · {f.userEmail}
          </div>
        </div>
        <div className={styles.feedbackChips}>
          <span className={`${ui.chip} ${f.kind === 'DEFECT' ? ui.chipNeedsWork : ui.chipSoft}`}>{t(`admin.feedback.kinds1.${f.kind}`)}</span>
          <span className={`${ui.chip} ${statusChipClass(f.status)}`}>{t(`feedback.status.${f.status}`)}</span>
        </div>
      </div>
      <p className={styles.feedbackBody}>{f.body}</p>
      {context && (
        <p className={styles.feedbackContext} data-testid="feedback-context">
          {context}
        </p>
      )}
      {f.closedAt && f.deletesOn && (
        <p className={styles.feedbackContext} data-testid="feedback-retention">
          {t('admin.feedback.closed', { closed: formatDate(f.closedAt, dateLocale()), deletes: formatDate(f.deletesOn, dateLocale()) })}
        </p>
      )}
      <div className={styles.feedbackAnswer}>
        <label className={ui.field}>
          <span className={ui.label}>{t('admin.feedback.statusLabel')}</span>
          <select className={`${ui.select} ${styles.narrow}`} value={status} onChange={(e) => setStatus(e.target.value as FeedbackStatus)}>
            {FEEDBACK_STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`feedback.status.${s}`)}
              </option>
            ))}
          </select>
        </label>
        <label className={`${ui.field} ${styles.feedbackNote}`}>
          <span className={ui.label}>{t('admin.feedback.note')}</span>
          <input className={ui.input} value={note} maxLength={2000} onChange={(e) => setNote(e.target.value)} />
        </label>
        <button
          type="button"
          className={`${ui.button} ${ui.primary}`}
          disabled={!changed || update.isPending}
          onClick={() => update.mutate({ id: f.id, status, note })}
        >
          {t('admin.panel.save')}
        </button>
        <button type="button" className={`${ui.button} ${styles.dangerOutline}`} onClick={() => setDeleting(true)}>
          {t('admin.feedback.delete')}
        </button>
      </div>
      {update.isError && <p className={styles.error}>{apiErrorText(update.error, t)}</p>}

      {deleting && (
      <Dialog open title={t('admin.feedback.deleteTitle')} onClose={() => setDeleting(false)} data-testid="delete-feedback-dialog">
        <div className={styles.form}>
          <p className={styles.noMargin}>{t('admin.feedback.deleteText', { name: f.userName })}</p>
          <blockquote className={styles.feedbackQuote}>{f.body.length > 200 ? `${f.body.slice(0, 200)}…` : f.body}</blockquote>
          {remove.isError && <p className={styles.error}>{apiErrorText(remove.error, t)}</p>}
          <div className={styles.buttonRow}>
            <button type="button" className={ui.button} onClick={() => setDeleting(false)}>
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className={`${ui.button} ${styles.danger}`}
              disabled={remove.isPending}
              onClick={() => remove.mutate(f.id, { onSuccess: () => setDeleting(false) })}
            >
              {t('admin.feedback.deleteConfirm')}
            </button>
          </div>
        </div>
      </Dialog>
      )}
    </article>
  );
}
